/**
 * Overdue Invoices Page
 * Main page for viewing and managing overdue invoices
 * US-INV-004: View Overdue Invoices
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { AlertCircle, Mail, Phone, Eye, DollarSign, MoreVertical, Download } from 'lucide-react';
import { invoiceService } from '@/services/commerce/invoiceService';
import { OverdueFilters } from '@/components/invoices/OverdueFilters';
import { SendReminderDialog } from '@/components/invoices/SendReminderDialog';
import { RecordPaymentDialog } from '@/components/invoices/RecordPaymentDialog';
import type { OverdueInvoiceSummary, OverdueInvoiceFilters, PagedOverdueResponse } from '@/types/invoice';
import { toast } from 'sonner';

export const OverdueInvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<PagedOverdueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<OverdueInvoiceFilters>({
    page: 0,
    size: 20,
  });
  const [selectedInvoice, setSelectedInvoice] = useState<OverdueInvoiceSummary | null>(null);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  useEffect(() => {
    loadOverdueInvoices();
  }, [filters]);

  const loadOverdueInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await invoiceService.getOverdueInvoices(filters);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load overdue invoices');
      console.error('Error loading overdue invoices:', err);
      toast.error('Failed to load overdue invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: OverdueInvoiceFilters) => {
    setFilters({ ...newFilters, page: 0 });
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleViewInvoice = (invoice: OverdueInvoiceSummary) => {
    navigate(`/invoices/${invoice.invoiceId}`);
  };

  const handleSendReminder = (invoice: OverdueInvoiceSummary) => {
    setSelectedInvoice(invoice);
    setShowReminderDialog(true);
  };

  const handleRecordPayment = (invoice: OverdueInvoiceSummary) => {
    setSelectedInvoice(invoice);
    setShowPaymentDialog(true);
  };

  const handleCallCustomer = (invoice: OverdueInvoiceSummary) => {
    if (invoice.customerPhone) {
      toast.info(`Call customer: ${invoice.customerName} at ${invoice.customerPhone}`);
      // In production, this could trigger a phone call via WebRTC or show a call dialog
    } else {
      toast.error('No phone number available for this customer');
    }
  };

  const handleExport = async (format: 'pdf' | 'excel') => {
    try {
      toast.loading(`Exporting ${format.toUpperCase()}...`);
      await invoiceService.exportOverdueReport(format);
      toast.success(`Report exported successfully as ${format.toUpperCase()}`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  const getColorClass = (daysOverdue: number): string => {
    if (daysOverdue < 7) return 'bg-yellow-50 hover:bg-yellow-100';
    if (daysOverdue <= 14) return 'bg-orange-50 hover:bg-orange-100';
    return 'bg-red-50 hover:bg-red-100';
  };

  const getUrgencyBadge = (daysOverdue: number): React.ReactNode => {
    if (daysOverdue < 7) {
      return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">Low</Badge>;
    }
    if (daysOverdue <= 14) {
      return <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">Medium</Badge>;
    }
    return <Badge variant="destructive">High</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <AlertCircle className="h-8 w-8 text-orange-500" />
            Overdue Invoices
          </h1>
          <p className="text-gray-600 mt-1">View and manage invoices requiring collection</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExport('pdf')} className="gap-2">
            <Download className="h-4 w-4" />
            Export PDF
          </Button>
          <Button variant="outline" onClick={() => handleExport('excel')} className="gap-2">
            <Download className="h-4 w-4" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <OverdueFilters onFilterChange={handleFilterChange} initialFilters={filters} />

      {/* Summary Stats */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Overdue</CardDescription>
              <CardTitle className="text-2xl">{data.totalElements}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Current Page</CardDescription>
              <CardTitle className="text-2xl">
                {data.currentPage + 1} of {data.totalPages}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Showing</CardDescription>
              <CardTitle className="text-2xl">{data.content.length} invoices</CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Overdue Invoices List</CardTitle>
          <CardDescription>
            Click on any row to view details. Color coding: Yellow (&lt;7 days), Orange (7-14 days), Red (&gt;14 days)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <AlertCircle className="h-12 w-12 text-red-500" />
              <p className="text-red-600">{error}</p>
              <Button onClick={loadOverdueInvoices}>Retry</Button>
            </div>
          ) : data && data.content.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-right">Amount Due</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="text-center">Days Overdue</TableHead>
                    <TableHead className="text-center">Urgency</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.content.map((invoice) => (
                    <TableRow
                      key={invoice.invoiceId}
                      className={`cursor-pointer ${getColorClass(invoice.daysOverdue)}`}
                      onClick={() => handleViewInvoice(invoice)}
                    >
                      <TableCell className="font-medium">{invoice.invoiceNumber}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{invoice.customerName}</p>
                          {invoice.customerEmail && (
                            <p className="text-xs text-gray-500">{invoice.customerEmail}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(invoice.balanceDue)}
                      </TableCell>
                      <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{invoice.daysOverdue} days</Badge>
                      </TableCell>
                      <TableCell className="text-center">{getUrgencyBadge(invoice.daysOverdue)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewInvoice(invoice);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendReminder(invoice);
                              }}
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              Send Reminder
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCallCustomer(invoice);
                              }}
                            >
                              <Phone className="h-4 w-4 mr-2" />
                              Call Customer
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRecordPayment(invoice);
                              }}
                            >
                              <DollarSign className="h-4 w-4 mr-2" />
                              Record Payment
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {data.totalPages > 1 && (
                <div className="mt-4">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => handlePageChange(Math.max(0, data.currentPage - 1))}
                          className={data.currentPage === 0 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                      {Array.from({ length: Math.min(5, data.totalPages) }, (_, i) => {
                        const page = i;
                        return (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => handlePageChange(page)}
                              isActive={page === data.currentPage}
                              className="cursor-pointer"
                            >
                              {page + 1}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}
                      <PaginationItem>
                        <PaginationNext
                          onClick={() => handlePageChange(Math.min(data.totalPages - 1, data.currentPage + 1))}
                          className={data.currentPage >= data.totalPages - 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <AlertCircle className="h-12 w-12 text-gray-400" />
              <p className="text-gray-600">No overdue invoices found</p>
              <p className="text-sm text-gray-500">Try adjusting your filters</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      {selectedInvoice && (
        <>
          <SendReminderDialog
            open={showReminderDialog}
            onOpenChange={setShowReminderDialog}
            invoice={selectedInvoice}
            onSuccess={() => {
              toast.success('Reminder sent successfully');
              setShowReminderDialog(false);
            }}
          />
          <RecordPaymentDialog
            open={showPaymentDialog}
            onOpenChange={setShowPaymentDialog}
            invoice={selectedInvoice}
            onSuccess={() => {
              toast.success('Payment recorded successfully');
              setShowPaymentDialog(false);
              loadOverdueInvoices();
            }}
          />
        </>
      )}
    </div>
  );
};
